import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  loadOgSettings,
  uploadOgImage,
  setOgImageUrl,
  saveOgTemplate,
  resetOgImage,
  saveOgPosition,
  type OgScope,
} from "@/lib/og-preview";
import { listPublishedPhotographers, type PhotographerOption } from "@/lib/admin-api";
import { fetchPicksForWeek } from "@/lib/selected-week-picks";
import { getISOWeek } from "@/lib/selected-weeks";

export const Route = createFileRoute("/admin/og-preview")({
  component: OgPreviewAdminPage,
});

type ExistingImage = { url: string; label: string };

function OgPreviewAdminPage() {
  const [photographers, setPhotographers] = useState<PhotographerOption[]>([]);
  const [weekPicks, setWeekPicks] = useState<ExistingImage[]>([]);

  useEffect(() => {
    listPublishedPhotographers().then(setPhotographers).catch(() => setPhotographers([]));
    const w = getISOWeek(new Date());
    fetchPicksForWeek(w.year, w.week)
      .then((picks) =>
        setWeekPicks(
          picks
            .filter((p) => !!p.image)
            .map((p) => ({ url: p.image, label: p.photographerName || "Selected" })),
        ),
      )
      .catch(() => setWeekPicks([]));
  }, []);

  const galleryImages: ExistingImage[] = photographers.flatMap((p) =>
    (p.gallery_urls ?? []).map((url, i) => ({ url, label: `${p.name} · Bild ${i + 1}` })),
  );

  return (
    <div>
      <div className="eyebrow text-muted-foreground">Magazin</div>
      <h1 className="serif text-4xl md:text-5xl mt-3">Link-Vorschau</h1>
      <p className="mt-3 text-sm text-muted-foreground max-w-xl">
        Lege fest, welches Bild beim Teilen von Links erscheint (Social Media, Messenger). Du kannst ein
        eigenes Bild hochladen, ein vorhandenes wählen oder eine gestaltete Vorlage mit eigenem Text erzeugen.
        Ohne Festlegung bleibt die bisherige Automatik aktiv.
      </p>

      <ScopeEditor
        scope="home"
        title="Startseite"
        hint="Standardmäßig wird automatisch das erste „Selected“-Bild der Woche verwendet."
        existing={[...weekPicks, ...galleryImages]}
      />
      <ScopeEditor
        scope="global"
        title="Globale Vorschau (übrige Seiten)"
        hint="Standardmäßig wird das feste Cover-Bild (og-cover.jpg) verwendet."
        existing={galleryImages}
      />
    </div>
  );
}

function ScopeEditor({
  scope,
  title,
  hint,
  existing,
}: {
  scope: OgScope;
  title: string;
  hint: string;
  existing: ExistingImage[];
}) {
  const [tab, setTab] = useState<"upload" | "existing" | "template">("upload");
  const [imageUrl, setImageUrl] = useState("");
  const [srcUrl, setSrcUrl] = useState("");
  const [focusX, setFocusX] = useState(50);
  const [focusY, setFocusY] = useState(50);
  const [natW, setNatW] = useState(1200);
  const [natH, setNatH] = useState(630);
  const [posDirty, setPosDirty] = useState(false);
  const [tplTitle, setTplTitle] = useState("");
  const [tplSubtitle, setTplSubtitle] = useState("");
  const [tplDesc, setTplDesc] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  function applySettings(s: Awaited<ReturnType<typeof loadOgSettings>>) {
    setImageUrl(s.imageUrl);
    setSrcUrl(s.srcUrl);
    setFocusX(s.focusX);
    setFocusY(s.focusY);
    setNatW(s.natW);
    setNatH(s.natH);
    setPosDirty(false);
    setTplTitle(s.title);
    setTplSubtitle(s.subtitle);
    setTplDesc(s.desc);
  }

  useEffect(() => {
    loadOgSettings(scope).then(applySettings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  function flash(msg: string) {
    setOk(msg);
    setTimeout(() => setOk(null), 2500);
  }

  async function onUpload(file: File) {
    setBusy("upload"); setError(null);
    try {
      await uploadOgImage(scope, file);
      applySettings(await loadOgSettings(scope));
      setReloadKey((k) => k + 1);
      flash("Bild hochgeladen und gesetzt.");
    } catch (e) { setError(e instanceof Error ? e.message : "Upload fehlgeschlagen."); }
    finally { setBusy(null); }
  }

  async function onChoose(url: string) {
    setBusy("choose"); setError(null);
    try {
      await setOgImageUrl(scope, url);
      applySettings(await loadOgSettings(scope));
      setReloadKey((k) => k + 1);
      flash("Vorschau gesetzt.");
    } catch (e) { setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen."); }
    finally { setBusy(null); }
  }

  async function onSavePosition() {
    setBusy("position"); setError(null);
    try {
      await saveOgPosition(scope, { focusX, focusY, natW, natH });
      setPosDirty(false);
      setReloadKey((k) => k + 1);
      flash("Bildausschnitt gespeichert.");
    } catch (e) { setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen."); }
    finally { setBusy(null); }
  }

  async function onSaveTemplate() {
    setBusy("template"); setError(null);
    try {
      await saveOgTemplate(scope, { title: tplTitle, subtitle: tplSubtitle, desc: tplDesc });
      applySettings(await loadOgSettings(scope));
      setReloadKey((k) => k + 1);
      flash("Vorlage erzeugt und gesetzt.");
    } catch (e) { setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen."); }
    finally { setBusy(null); }
  }

  async function onReset() {
    setBusy("reset"); setError(null);
    try {
      await resetOgImage(scope);
      applySettings(await loadOgSettings(scope));
      setReloadKey((k) => k + 1);
      flash("Zurückgesetzt – Automatik aktiv.");
    } catch (e) { setError(e instanceof Error ? e.message : "Zurücksetzen fehlgeschlagen."); }
    finally { setBusy(null); }
  }

  const previewSrc = imageUrl ? `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}r=${reloadKey}` : "";

  return (
    <section className="mt-10 border border-border p-5 md:p-6">
      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-5">
          <h2 className="serif text-2xl">{title}</h2>
          <p className="mt-2 text-xs text-muted-foreground">{hint}</p>
          <div className="eyebrow text-muted-foreground mt-5 mb-2">Aktuelle Vorschau</div>
          {srcUrl ? (
            <>
              <div className="space-y-3">
                <div>
                  <div className="eyebrow text-muted-foreground mb-1 text-[10px]">Desktop / Link (1200 × 630)</div>
                  <img
                    src={srcUrl}
                    alt={`Link-Vorschau ${title} – Desktop`}
                    style={{ objectPosition: `${focusX}% ${focusY}%` }}
                    className="w-full aspect-[1200/630] object-cover bg-muted border border-border"
                  />
                </div>
                <div>
                  <div className="eyebrow text-muted-foreground mb-1 text-[10px]">Mobil / Hochformat (4 : 5)</div>
                  <img
                    src={srcUrl}
                    alt={`Link-Vorschau ${title} – Mobil`}
                    style={{ objectPosition: `${focusX}% ${focusY}%` }}
                    className="w-1/2 aspect-[4/5] object-cover bg-muted border border-border"
                  />
                </div>
              </div>
              <div className="mt-3 space-y-2">
                <label className="block">
                  <div className="eyebrow text-muted-foreground mb-1">Horizontal ({focusX}%)</div>
                  <input type="range" min={0} max={100} value={focusX}
                    onChange={(e) => { setFocusX(Number(e.target.value)); setPosDirty(true); }}
                    className="w-full" />
                </label>
                <label className="block">
                  <div className="eyebrow text-muted-foreground mb-1">Vertikal ({focusY}%)</div>
                  <input type="range" min={0} max={100} value={focusY}
                    onChange={(e) => { setFocusY(Number(e.target.value)); setPosDirty(true); }}
                    className="w-full" />
                </label>
                <button onClick={onSavePosition} disabled={!!busy || !posDirty}
                  className="eyebrow bg-foreground text-background px-4 py-2 disabled:opacity-50">
                  {busy === "position" ? "Speichert …" : "Bildausschnitt speichern"}
                </button>
              </div>
            </>
          ) : previewSrc ? (
            <img
              key={previewSrc}
              src={previewSrc}
              alt={`Link-Vorschau ${title}`}
              className="w-full aspect-[1200/630] object-cover bg-muted border border-border"
            />
          ) : (
            <div className="w-full aspect-[1200/630] bg-muted border border-border flex items-center justify-center text-xs text-muted-foreground px-4 text-center">
              Keine manuelle Vorschau – Automatik/Standard aktiv.
            </div>
          )}
          {imageUrl && (
            <button onClick={onReset} disabled={!!busy}
              className="eyebrow text-xs border border-border px-3 py-2 mt-3 hover:border-destructive hover:text-destructive disabled:opacity-50">
              {busy === "reset" ? "…" : "Zurücksetzen (Automatik)"}
            </button>
          )}
        </div>

        <div className="md:col-span-7">
          {error && <div className="mb-4 border border-destructive text-destructive p-3 text-sm">{error}</div>}
          {ok && <div className="mb-4 border border-border p-3 text-sm">{ok}</div>}

          <div className="flex gap-2 border-b border-border">
            {([
              ["upload", "Hochladen"],
              ["existing", "Vorhandenes Bild"],
              ["template", "Vorlage generieren"],
            ] as const).map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)}
                className={`eyebrow text-xs px-3 py-2 -mb-px border-b-2 ${tab === id ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                {label}
              </button>
            ))}
          </div>

          {tab === "upload" && (
            <div className="mt-4">
              <input type="file" accept="image/jpeg,image/png,image/webp"
                disabled={!!busy}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); }}
                className="block w-full text-sm" />
              <p className="mt-2 text-xs text-muted-foreground">Empfohlen: 1200 × 630 px, JPG/PNG/WEBP, max. 8 MB.</p>
              {busy === "upload" && <div className="mt-2 text-xs text-muted-foreground">Lädt hoch …</div>}
            </div>
          )}

          {tab === "existing" && (
            <div className="mt-4">
              {existing.length === 0 ? (
                <div className="text-sm text-muted-foreground">Keine Bilder verfügbar.</div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[360px] overflow-y-auto pr-1">
                  {existing.map((img) => (
                    <button key={img.url} onClick={() => onChoose(img.url)} disabled={!!busy}
                      title={img.label}
                      className={`relative aspect-[4/3] overflow-hidden border ${imageUrl === img.url ? "border-foreground" : "border-border"} hover:border-foreground disabled:opacity-50`}>
                      <img src={img.url} alt={img.label} className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "template" && (
            <div className="mt-4 space-y-3">
              <label className="block">
                <div className="eyebrow text-muted-foreground mb-1">Titel</div>
                <input value={tplTitle} onChange={(e) => setTplTitle(e.target.value)}
                  placeholder="OM Selected"
                  className="w-full border border-border bg-transparent p-2 text-sm" />
              </label>
              <label className="block">
                <div className="eyebrow text-muted-foreground mb-1">Untertitel</div>
                <input value={tplSubtitle} onChange={(e) => setTplSubtitle(e.target.value)}
                  placeholder="Kuratiertes Magazin für Fotografie"
                  className="w-full border border-border bg-transparent p-2 text-sm" />
              </label>
              <label className="block">
                <div className="eyebrow text-muted-foreground mb-1">Beschreibung</div>
                <input value={tplDesc} onChange={(e) => setTplDesc(e.target.value)}
                  placeholder="Kurze Beschreibung …"
                  className="w-full border border-border bg-transparent p-2 text-sm" />
              </label>
              <button onClick={onSaveTemplate} disabled={!!busy}
                className="eyebrow bg-foreground text-background px-4 py-2 disabled:opacity-50">
                {busy === "template" ? "Erzeuge …" : "Vorlage erzeugen & setzen"}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}